import type { SidebarDestination } from '@alison/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { SxProps, Theme } from '@mui/material/styles';
import { type ReactNode, useEffect, useMemo } from 'react';
import { dateWindowFromRange } from '../lib/dateUtils';
import { useMetrics } from '../lib/filters.hooks';
import { type BoardName, type BoardRoute, useMediaSearch } from '../lib/media-search';
import { useAuthenticatedSession } from '../lib/useAuthenticatedSession';
import { useShellAccount } from '../lib/useShellAccount';
import { AppShell } from './AppShell';
import type { ScopeState } from './filters/types';
import { useScopeSelection } from './filters/useScopeSelection';
import type { MediaFilters, MediaKpi } from './media/data/types';
import { availableKpis } from './media/data/utils';
import { MediaFilterBar } from './media/MediaFilterBar';

const BOARD_NAME_BY_ROUTE: Record<BoardRoute, BoardName> = {
  '/dashboard': 'dashboard',
  '/media': 'media',
  '/explore': 'explore',
  '/elements-explorer': 'elements',
};

export interface BoardScope {
  scope: ScopeState;
  applyScope: (scope: ScopeState) => void;
  filters: MediaFilters;
  setFilters: (filters: MediaFilters) => void;
  accountIds: number[];
  dateWindow: [string, string];
  kpiOptions: MediaKpi[];
}

interface BoardPageProps {
  route: BoardRoute;
  active: SidebarDestination;
  contentSx?: SxProps<Theme>;
  topBar?: (board: BoardScope) => ReactNode;
  topBarActions?: (board: BoardScope) => ReactNode;
  children: (board: BoardScope) => ReactNode;
}

function BoardPageContent({
  route,
  active,
  contentSx,
  topBar,
  topBarActions,
  children,
}: BoardPageProps): ReactNode {
  const { session, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  const {
    filters,
    setFilters,
    scope: urlScope,
    setScope: setUrlScope,
    hasScope,
  } = useMediaSearch(route);
  const { scope, applyScope, accountIds } = useScopeSelection({
    scope: urlScope,
    onScopeChange: setUrlScope,
    urlHasScope: hasScope,
  });
  // Scope KPI availability to the selected accounts so the list drops metrics the
  // in-view network doesn't support (e.g. VCR on a network without video completions).
  const { data: metricsDict } = useMetrics(accountIds);
  const today = useMemo(() => new Date(), []);
  const dateWindow = dateWindowFromRange(scope.range, today);
  const kpiOptions = useMemo(() => availableKpis(metricsDict), [metricsDict]);

  useEffect(() => {
    const fallback = kpiOptions[0];
    if (metricsDict && fallback && !kpiOptions.some((option) => option.id === filters.kpi)) {
      setFilters({ ...filters, kpi: fallback.id });
    }
  }, [metricsDict, kpiOptions, filters, setFilters]);

  const board: BoardScope = {
    scope,
    applyScope,
    filters,
    setFilters,
    accountIds,
    dateWindow,
    kpiOptions,
  };

  return (
    <AppShell
      active={active}
      account={account}
      onLogout={() => void onSignOut()}
      {...(contentSx === undefined ? {} : { sx: contentSx })}
      topBarActions={topBarActions?.(board)}
      topBar={
        topBar ? (
          topBar(board)
        ) : (
          <MediaFilterBar
            bar={BOARD_NAME_BY_ROUTE[route]}
            scope={scope}
            onScopeChange={applyScope}
            filters={filters}
            onFiltersChange={setFilters}
            dateWindow={dateWindow}
            accountIds={accountIds}
            kpiOptions={kpiOptions}
          />
        )
      }
    >
      {children(board)}
    </AppShell>
  );
}

export function BoardPage(props: BoardPageProps): ReactNode {
  const { session, isPending } = useAuthenticatedSession();

  if (isPending || session === null) {
    return (
      <Box
        sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <BoardPageContent {...props} />;
}
